import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiMail, FiMapPin, FiPhone } from "react-icons/fi";
import { PiBuildingLight } from "react-icons/pi";
import { useSelector } from "react-redux";
import { Modal, ModalBody } from "reactstrap";

import BarLoader from "@components/shared/BarLoader/BarLoader";
import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import {
  PAYMENT_OPTIONS,
  PAYMENT_STATUS,
  REFUND_STATUS,
  SHOW_BOOKING_TYPES,
  SHOW_HIDDEN_BOOKING_TYPES,
  USER_ROLE,
} from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { DateFormats } from "@enums/shared.enum";
import { useUserProfile } from "@hooks/useUserProfile/useUserProfile";
import {
  BookingDetailsType,
  ReservationDetailsType,
} from "@models/components/user.model";
import { Participant } from "@models/services/booking";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import { RootState } from "@store/index";
import { formatPrice } from "@utils/common.utils";

import BookedAmenities from "../BookedAmenities/BookedAmenities";
import BookedSlot from "../BookedSlot/BookedSlot";
import DetailBox from "../DetailBox/DetailBox";
import PaymentMethod from "../PaymentMethod/PaymentMethod";
import PriceBlock from "../PriceBlock/PriceBlock";

const ReservationDetails: React.FC<ReservationDetailsType> = ({
  bookingData,
  isKiosk = false,
  isOpen,
  kioskButtonComponent,
  showCancelSection = true,
  toggleCancelModal,
  toggleOpen,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector((state: RootState) => state.local);
  const { bookingId } = bookingData;
  const [bookingDetails, setBookingDetails] = useState<BookingDetailsType>(
    {} as BookingDetailsType
  );
  const accessToken = useSelector(
    (state: RootState) => state.local.accessToken
  );
  const { data: userProfile } = useUserProfile(accessToken, {
    enabled: isAuthenticated && !!accessToken,
    staleTime: Infinity, // profile is stable — reuse cache, never refetch automatically
  });
  const currentUserId = userProfile?.profile?.id;
  const [isFetching, setIsFetching] = useState(false);
  const [isInvoice, setIsInvoice] = useState(false);
  const [isPaymentStatusLoader, setIsPaymentStatusLoader] = useState(true);
  const location = {
    latitude: bookingDetails?.location?.map?.latitude,
    location: bookingDetails?.location?.mapLocation,
    longitude: bookingDetails?.location?.map?.longitude,
  };

  const bookedTimeSlot = bookingDetails?.timeSlot
    ? `${bookingDetails?.timeSlot} ${t("min")}`
    : "";

  const slotTypeValue = bookedTimeSlot;

  const isShowDeleteButton =
    bookingDetails?.role === USER_ROLE.ORGANIZER_ROLE &&
    moment(bookingDetails?.startDate)
      .startOf(DateFormats.Day)
      .isSameOrAfter(moment().startOf(DateFormats.Day)) &&
    !bookingDetails?.bookingCanceled &&
    bookingDetails?.bookingCancelable;

  const isShowWarningMsg =
    !bookingDetails?.bookingCancelable && bookingDetails?.reason;

  const isShowCancelReason = !isShowWarningMsg && isShowDeleteButton;

  const hasRefund = Boolean(bookingDetails?.hasRefund);

  const contactEmail = bookingDetails?.location?.contactEmail || "";

  const contactPhone = bookingDetails?.location?.contactPhone || "";

  const getUserBookingDetailsFn = async () => {
    setIsFetching(true);
    const data =
      isAuthenticated && isKiosk
        ? await API_SERVICES.USER.GET_USER_BOOKING_DETAILS(bookingId)
        : await API_SERVICES.USER.GET_PUBLIC_BOOKING_DETAILS(bookingId);
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setBookingDetails(responseData.data);
      const participants = responseData.data?.participants as Participant[];
      const isCurrentUserOrganizer =
        Boolean(isAuthenticated) &&
        responseData?.data?.type === CONSTANTS.IS_BOOKING &&
        participants?.some(
          (participant: Participant) =>
            participant.isOrganizer === CONSTANTS.IS_CURRENT_USER &&
            participant.id === currentUserId
        ) === true;
      setIsFetching(false);
      if (isCurrentUserOrganizer) {
        paymentDataStatus();
      } else {
        setIsPaymentStatusLoader(false);
      }
    } else setIsFetching(false);
  };

  const paymentDataStatus = async () => {
    const paymentData =
      await API_SERVICES.USER.GET_USER_UPDATE_PAYMENT_STATUS(bookingId);
    setIsInvoice(paymentData.data?.invoice_status);
    if (paymentData.data?.invoice_status) {
      setBookingDetails(prev => ({
        ...prev,
        creditNoteCreated: !!paymentData.data?.credit_note_created,
        isPartialPayment: !!paymentData.data?.is_partial_payment,
        paymentStatus: paymentData.data?.invoice_status,
      }));
    }
    setIsPaymentStatusLoader(false);
  };
  const formatAddress = (address?: string) =>
    address
      ? address.split("\n").map((line, i) => (
          <span className="mb-2 d-block" key={i}>
            {line}
          </span>
        ))
      : "";
  const getPaymentStatus = (bookingDetails: BookingDetailsType) => {
    let status = "";

    const paymentStatus = bookingDetails?.paymentStatus ?? "";

    // Refund status overrides the payment badge when present
    if (bookingDetails?.refundStatus) {
      switch (bookingDetails.refundStatus) {
        case REFUND_STATUS.INITIATED:
          return "refundInitiated";
        case REFUND_STATUS.SUCCESS:
          return "refundSuccess";
        case REFUND_STATUS.FAIL:
          return "refundFailed";
      }
    }

    if (paymentStatus.toUpperCase() === PAYMENT_STATUS.SUCCESS) {
      status = PAYMENT_STATUS.PAID;

      if (bookingDetails?.isPartialPayment) status = PAYMENT_STATUS.PARTPAYMENT;
    } else if (
      paymentStatus.toUpperCase() === PAYMENT_STATUS.PAYMENT_SCHEDULED ||
      paymentStatus.toUpperCase() === PAYMENT_STATUS.SCHEDULED
    ) {
      if (bookingDetails?.bookingCanceled) {
        status = PAYMENT_STATUS.CANCELLED;
      } else {
        status = PAYMENT_STATUS.SCHEDULED;
      }
    }

    return (status || paymentStatus).toLowerCase();
  };

  const getPaymentScheduledDate = (bookingDetails: BookingDetailsType) => {
    const rawScheduled = bookingDetails?.paymentScheduledDate;
    if (!rawScheduled) return "";
    const dateStr =
      typeof rawScheduled === "object" &&
      rawScheduled !== null &&
      "date" in rawScheduled
        ? rawScheduled.date
        : typeof rawScheduled === "string"
          ? rawScheduled
          : "";
    if (!dateStr) return "";
    const m = moment(dateStr);
    if (!m.isValid()) return "";
    return `${m.format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT)}, ${m.format(CONSTANTS.DATE_FORMATS.HOUR_MINUTE_FORMAT)}`;
  };

  const isPaymentScheduled =
    bookingDetails?.paymentStatus?.toUpperCase() ===
      PAYMENT_STATUS.PAYMENT_SCHEDULED ||
    bookingDetails?.paymentStatus?.toUpperCase() === PAYMENT_STATUS.SCHEDULED;

  const paymentScheduledDate = useMemo(() => {
    if (!isPaymentScheduled) return "";
    return getPaymentScheduledDate(bookingDetails);
  }, [isPaymentScheduled, bookingDetails?.paymentScheduledDate]);

  const getBadgeClass = (type?: string) => {
    switch (type) {
      case "maintenance":
        return "fg-event-maintenance";
      case "training":
        return "fg-event-training";
      case "camp_tournament":
        return "fg-event-camp-tournament";
      case "exception":
        return "fg-event-exception";
      case "blocking":
        return "fg-event-blocking";
      default:
        return "fg-event-booking";
    }
  };

  useEffect(() => {
    getUserBookingDetailsFn();
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleOpen}
      data-testid="reservation-details-modal"
      id="reservation-details-modal"
      centered={true}
    >
      <Loader isLoading={isFetching} />
      <ModalBody>
        <>
          <h5>{applyLocalization(bookingDetails?.club?.title)}</h5>
          <button
            className="fg-close-btn"
            data-testid="closeButton"
            aria-label="Close"
            onClick={toggleOpen}
          >
            <i className="fa-sharp fa-solid fa-xmark"></i>
          </button>
          <div className="mb-1">
            {t("bookedOn")}:
            <span className="ms-2">
              {moment(bookingDetails?.startDate).format(
                CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
              )}
            </span>
          </div>
          {bookingDetails?.passcode != null && (
            <div>
              {t("passcode")}:
              <span className="ms-2">{bookingDetails.passcode}</span>
            </div>
          )}
          {bookingDetails?.type &&
            SHOW_BOOKING_TYPES.includes(bookingDetails?.type) &&
            bookingDetails?.title != null && (
              <div className="mt-2">
                <span
                  className={`badge rounded-pill text-white px-2 py-1 ${getBadgeClass(bookingDetails?.type)}`}
                >
                  {applyLocalization(bookingDetails?.title)}
                </span>
              </div>
            )}
          <hr className="mt-3 mb-3" />
          <div className="d-flex">
            <div>
              <div className="d-flex gap-2 mb-2">
                <h6>
                  <FiMapPin className="me-2 mb-1" />
                  {applyLocalization(bookingDetails?.location?.title)}
                  <DetailBox
                    label=""
                    value={formatAddress(
                      applyLocalization(bookingDetails?.location?.address)
                    )}
                    location={location}
                  />
                </h6>
              </div>
              <div className="d-flex gap-2">
                <PiBuildingLight />
                <h6>{applyLocalization(bookingDetails?.resource?.title)}</h6>
              </div>

              {bookingDetails?.guests > 0 && (
                <div>
                  {" "}
                  {t("guestCount")}: {bookingDetails?.guests}
                </div>
              )}
            </div>
            <div className="ms-auto flex-shrink-0">
              {bookingDetails?.startTime &&
                bookingDetails?.endTime &&
                slotTypeValue && (
                  <BookedSlot
                    showHeading={false}
                    startTime={bookingDetails?.startTime || ""}
                    endTime={bookingDetails?.endTime || ""}
                    slotType={slotTypeValue}
                  />
                )}
            </div>
          </div>

          <div className="bg-light p-2">
            {isPaymentStatusLoader ? (
              <BarLoader />
            ) : (
              (bookingDetails?.paymentStatus ||
                (bookingDetails?.payment?.type &&
                  bookingDetails?.payment?.type !== PAYMENT_OPTIONS.NONE)) && (
                <div className="mb-3">
                  {bookingDetails?.payment?.type &&
                    bookingDetails?.payment?.type !== PAYMENT_OPTIONS.NONE && (
                      <>
                        <h6>{t("paymentMethod")}</h6>
                        <div className="d-flex gap-2">
                          {bookingDetails?.payment?.type &&
                            bookingDetails?.payment?.type !==
                              PAYMENT_OPTIONS.NONE && (
                              <div className="payment-type-wrapper">
                                <PaymentMethod
                                  label=""
                                  value={bookingDetails?.payment?.type}
                                />
                              </div>
                            )}
                        </div>
                      </>
                    )}

                  {bookingDetails?.paymentStatus && (
                    <div
                      className="mt-2"
                      data-testid="paymentStatus-test-wrapper"
                    >
                      <h6>{t("paymentStatus")}</h6>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {bookingDetails?.paymentStatus && (
                          <div className="payment-status-wrapper">
                            <PaymentMethod
                              isStatus
                              label=""
                              value={getPaymentStatus(bookingDetails)}
                            />
                          </div>
                        )}

                        {paymentScheduledDate && (
                          <span className="fg-FS-14">
                            {t("on")} {paymentScheduledDate}
                          </span>
                        )}

                        {isInvoice && (
                          <>
                            {bookingDetails?.isPartialPayment && (
                              <div className="payment-status-wrapper">
                                <PaymentMethod
                                  isStatus
                                  label=""
                                  value={PAYMENT_STATUS.PARTPAYMENT.toLowerCase()}
                                />
                              </div>
                            )}

                            {bookingDetails?.creditNoteCreated && (
                              <div className="payment-status-wrapper">
                                <PaymentMethod
                                  isStatus
                                  label=""
                                  value={PAYMENT_STATUS.CREDITNOTE.toLowerCase()}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
            {bookingDetails?.resource && (
              <div className="fg-booked-amenities-wrapper mt-2">
                {bookingDetails?.paymentStatus && (
                  <div className="fg-dashed-divider" />
                )}
                <div className="d-flex align-items-center justify-content-between mb-2 mt-2">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <div className="fg-FS-14 fg-text-primary font-weight-light">
                      {applyLocalization(bookingDetails?.resource?.title)}
                      {bookingDetails?.participants &&
                        bookingDetails.participants.length > 1 &&
                        ` + ${t("guests")}`}
                    </div>
                    {(() => {
                      if (!bookingDetails?.vatPercentage) return null;
                      const vatPercentageNum = Number.parseFloat(
                        bookingDetails.vatPercentage
                          .toString()
                          .replace("%", "") || "0"
                      );
                      if (
                        vatPercentageNum <= 0 &&
                        bookingDetails?.vatHandlingType === "EXCLUSIVE"
                          ? (bookingDetails?.basePrice ?? 0)
                          : (bookingDetails?.bookingAmount ?? 0) <= 0
                      )
                        return null;
                      const badgeClass =
                        vatPercentageNum > CONSTANTS.VAT_STANDARD_RATE
                          ? "fg-vat-high"
                          : "fg-vat-standard";
                      return (
                        <span
                          className={`badge rounded-pill fg-FS-10 px-2 py-1 fg-vat-badge ${badgeClass}`}
                        >
                          {bookingDetails.vatPercentage.includes("%")
                            ? bookingDetails.vatPercentage
                            : `${bookingDetails.vatPercentage}%`}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-end">
                    <div className="fg-FS-14 fg-text-primary font-weight-light">
                      {formatPrice(
                        bookingDetails?.bookingAmount,
                        bookingDetails?.currency || ""
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <BookedAmenities bookingDetails={bookingDetails} />
            <div className="price-block mt-2">
              <PriceBlock bookingDetails={bookingDetails} />
            </div>
          </div>

          {!SHOW_HIDDEN_BOOKING_TYPES.includes(bookingDetails?.type ?? "") && (
            <div className="price-block mt-2 bg-light p-2">
              <div className="organiser-block">
                <h5>{t("organiser")}</h5>
                {bookingDetails?.participants
                  ?.filter(p => p.isOrganizer === 1)
                  .map((org, idx) => (
                    <div key={`org-${idx}`}>{org.userName}</div>
                  ))}
              </div>

              {bookingDetails?.participants?.length > 1 && (
                <div className="guest-block mt-3">
                  <h5>{t("guests")}</h5>
                  {bookingDetails?.participants
                    ?.filter(p => p.isOrganizer === 0)
                    .map((guest, idx) => (
                      <div key={`guest-${idx}`}>{guest.userName}</div>
                    ))}
                </div>
              )}
            </div>
          )}
        </>
        <>
          {!SHOW_HIDDEN_BOOKING_TYPES.includes(bookingDetails?.type ?? "") && (
            <hr className="mt-3 mb-1" />
          )}
          {isShowWarningMsg && showCancelSection && (
            <div className="mt-4">
              <WarningMessageBox message={bookingDetails?.reason} />
            </div>
          )}
          {isShowCancelReason && showCancelSection && (
            <div className="mt-4">
              <WarningMessageBox
                message={
                  hasRefund
                    ? `${t(
                        "cancelBookingWithRefundUntil",
                        "Cancellation and refund are available for this booking until"
                      )} ${moment(
                        bookingDetails?.cancellationUntil?.date
                      ).format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT)}.`
                    : `${t("cancelBookingUntil")} ${moment(
                        bookingDetails?.cancellationUntil?.date
                      ).format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT)}`
                }
              />
            </div>
          )}{" "}
          {(bookingDetails?.schedulePaymentFailed ||
            bookingData?.schedulePaymentFailed) && (
            <div className="mt-4" data-testid="schedule-payment-failed-box">
              <WarningMessageBox
                className="bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25"
                message={
                  <div className="d-flex flex-column gap-2 text-danger">
                    <div className="d-flex align-items-start gap-2">
                      <FiAlertCircle
                        className="flex-shrink-0 mt-1 text-danger"
                        size={18}
                      />
                      <span className="text-danger">
                        {t(
                          "schedulePaymentFailedMsg",
                          "Your payment has failed. Please contact the admin to make a payment."
                        )}
                      </span>
                    </div>
                    {(contactEmail || contactPhone) && (
                      <div className="d-flex flex-wrap align-items-center gap-2 pt-2 border-top border-danger border-opacity-25">
                        {contactEmail && (
                          <a
                            href={`mailto:${contactEmail}`}
                            className="btn btn-sm btn-light bg-white border border-danger border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 text-decoration-none shadow-sm text-danger"
                            title={contactEmail}
                          >
                            <FiMail className="flex-shrink-0 text-danger" />
                            <span className="text-danger">{contactEmail}</span>
                          </a>
                        )}
                        {contactPhone && (
                          <a
                            href={`tel:${contactPhone}`}
                            className="btn btn-sm btn-light bg-white border border-danger border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 text-decoration-none shadow-sm text-danger"
                            title={String(contactPhone)}
                          >
                            <FiPhone className="flex-shrink-0 text-danger" />
                            <span className="text-danger">{contactPhone}</span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                }
              />
            </div>
          )}
          {isShowDeleteButton && showCancelSection && (
            <button
              className="btn fg-btn fg-btn-grey mt-0"
              onClick={() => toggleCancelModal(true)}
              disabled={!bookingDetails?.bookingCancelable}
            >
              {t("cancelBooking")}
            </button>
          )}
          {isKiosk && kioskButtonComponent}
        </>
      </ModalBody>
    </Modal>
  );
};

export default ReservationDetails;
